/**
 * Vaste mobiele bottom-navigatie voor de PWA. Rol-conditioneel.
 * - Alleen zichtbaar op mobiel (`lg:hidden`).
 * - Exact 5 items per rol conform product-spec.
 * - Actieve status via useLocation → subpath-match.
 * - Safe-area-inset padding zodat de iPhone home-indicator niet overlapt.
 */
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, ClipboardList, MessageSquare, CalendarDays, MoreHorizontal, Users, FileEdit } from "lucide-react";

const OUDER_ITEMS = [
  { key: "home",       label: "Home",       icon: Home,          to: "/portaal" },
  { key: "dagverslag", label: "Dagverslag", icon: FileEdit,      to: "/portaal/dagverslag" },
  { key: "berichten",  label: "Berichten",  icon: MessageSquare, to: "/portaal/berichten" },
  { key: "planning",   label: "Planning",   icon: CalendarDays,  to: "/portaal/planning" },
  { key: "meer",       label: "Meer",       icon: MoreHorizontal, to: "/portaal/meer" },
];

const STAFF_ITEMS = [
  { key: "presentie",  label: "Presentie",  icon: ClipboardList, to: "/app/planning" },
  { key: "kinderen",   label: "Kinderen",   icon: Users,         to: "/app/kinderen" },
  { key: "dagverslag", label: "Dagverslag", icon: FileEdit,      to: "/app/communicatie" },
  { key: "berichten",  label: "Berichten",  icon: MessageSquare, to: "/app/communicatie" },
  { key: "meer",       label: "Meer",       icon: MoreHorizontal, to: "/app/mijn-rooster" },
];

export default function MobileBottomNav() {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) return null;
  // Public / auth routes: geen bottom-nav.
  const path = location.pathname;
  if (path.startsWith("/login") || path.startsWith("/inschrijven") || path.startsWith("/auth")) return null;

  const items = user.role === "ouder" ? OUDER_ITEMS : STAFF_ITEMS;

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-neutral-200 pb-[env(safe-area-inset-bottom)]"
      aria-label="Hoofdnavigatie"
      data-testid="mobile-bottom-nav"
    >
      <ul className="grid grid-cols-5">
        {items.map((it) => {
          const Icon = it.icon;
          const active = path === it.to || path.startsWith(it.to + "/");
          return (
            <li key={it.key}>
              <NavLink
                to={it.to}
                data-testid={`mobile-nav-${it.key}`}
                className={
                  "flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium min-h-[56px] " +
                  (active ? "text-[color:var(--sage)]" : "text-neutral-500 hover:text-neutral-800")
                }
                aria-current={active ? "page" : undefined}
              >
                <Icon className={`h-5 w-5 ${active ? "" : "opacity-80"}`} aria-hidden="true" />
                <span>{it.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
